import { debounce } from 'lodash';
import PropTypes from 'prop-types';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import {
  DEBOUNCE_SEARCH_INPUT,
  ROUTE_SEARCH,
  TERM_KEY_PARAM,
} from '@/common/constants';
import {
  getNextUrlQuery,
  getSearchParam,
} from '@/common/helpers/URLQueryHelpers';

const SearchContext = createContext(null);

export const SearchContextProvider = ({ children }) => {
  const history = useHistory();
  const location = useLocation();
  const isSearchPage = location.pathname.includes(ROUTE_SEARCH);
  const URLSearchParamsInstance = new URLSearchParams(location.search);
  const term = getSearchParam(TERM_KEY_PARAM, URLSearchParamsInstance);
  const [localTerm, setLocalTerm] = useState(term ?? '');
  const [autocompleteTerm, setAutocompleteTerm] = useState(term ?? '');
  const debouncedSetAutocompleteTerm = useMemo(
    () =>
      debounce((value) => {
        setAutocompleteTerm(value);
      }, DEBOUNCE_SEARCH_INPUT),
    [],
  );

  const handleSetLocalTerm = useCallback(
    (nextTerm) => {
      debouncedSetAutocompleteTerm(nextTerm);
      setLocalTerm(nextTerm);
    },
    [debouncedSetAutocompleteTerm],
  );

  useEffect(() => {
    if (isSearchPage) {
      handleSetLocalTerm(term ?? '');
    }
  }, [term, isSearchPage, handleSetLocalTerm]);

  const setSearchParams = (newTerm) => {
    handleSetLocalTerm(newTerm ?? '');
    history.push({
      pathname: ROUTE_SEARCH,
      search: getNextUrlQuery(
        { [TERM_KEY_PARAM]: newTerm ?? null },
        URLSearchParamsInstance,
        false,
      ),
    });
  };

  return (
    <SearchContext.Provider
      value={{
        autocompleteTerm,
        localTerm,
        setLocalTerm: handleSetLocalTerm,
        setSearchParams,
        term,
      }}
    >
      {children}
    </SearchContext.Provider>
  );
};

export const useSearchContext = () => useContext(SearchContext);

SearchContextProvider.defaultProps = {};

SearchContextProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};
